import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Wallet, TrendingUp, PiggyBank,
  Layers, Target, CreditCard, Calendar, Users,
  ChevronDown, BarChart2, BookOpen, LogOut,
  Settings, FileBarChart, X,
  Sparkles, RefreshCw, TrendingDown,
} from 'lucide-react';

// ── Icon registry ─────────────────────────────────────────────────────────────
const ICONS = {
  overview:   LayoutDashboard,
  budget:     Wallet,
  networth:   TrendingUp,
  investing:  BarChart2,
  retirement: PiggyBank,
  rsus:       Layers,
  goals:      Target,
  credit:     CreditCard,
  calendar:   Calendar,
  family:     Users,
  recurring:  RefreshCw,
  debt:       TrendingDown,
  ai:         Sparkles,
  learn:      BookOpen,
  reports:    FileBarChart,
  settings:   Settings,
};

// ── Group definitions ─────────────────────────────────────────────────────────
const GROUPS = [
  { id: 'dashboard', label: 'Dashboard', items: ['overview'] },
  { id: 'money',     label: 'Money',     items: ['budget', 'recurring'] },
  { id: 'wealth',    label: 'Wealth',    items: ['networth', 'investing', 'retirement', 'rsus'] },
  { id: 'planning',  label: 'Planning',  items: ['goals', 'debt', 'credit'] },
  { id: 'life',      label: 'Life',      items: ['calendar', 'family'] },
  { id: 'tools',     label: 'Tools',     items: ['ai', 'learn', 'reports'] },
];

// Items always pinned below the groups (never in a collapsible section)
const PINNED_BOTTOM = ['settings'];

function getGroupId(tabId) {
  return GROUPS.find(g => g.items.includes(tabId))?.id ?? null;
}

// ── Animated group body ───────────────────────────────────────────────────────
// Uses the CSS Grid 0fr→1fr trick so the animation is perfectly smooth in both
// directions without needing to measure DOM heights.
function GroupBody({ open, children }) {
  return (
    <div
      className="overflow-hidden transition-[grid-template-rows] duration-300 ease-in-out"
      style={{
        display: 'grid',
        gridTemplateRows: open ? '1fr' : '0fr',
        transition: 'grid-template-rows 280ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* min-h-0 is required for the 0fr row to actually collapse */}
      <div style={{ minHeight: 0, overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
}

// ── Main Sidebar ──────────────────────────────────────────────────────────────
export default function Sidebar({ activeTab, setActiveTab, tabs, onSignOut, user, isOpen, onClose }) {
  const displayName =
    user?.user_metadata?.first_name ||
    user?.email?.split('@')[0] ||
    'You';
  const displaySub = user?.email || 'finovo account';

  // Build id → tab lookup so we can resolve group items defensively
  const tabById = Object.fromEntries((tabs || []).map(t => [t.id, t]));

  // Which groups are currently expanded — default: only the active tab's group
  const [openGroups, setOpenGroups] = useState(() => {
    const g = getGroupId(activeTab);
    return new Set(g ? [g] : [GROUPS[0].id]);
  });

  // When active tab changes (external / programmatic), ensure its group is open
  useEffect(() => {
    const g = getGroupId(activeTab);
    if (g) {
      setOpenGroups(prev => {
        if (prev.has(g)) return prev;
        return new Set([...prev, g]);
      });
    }
  }, [activeTab]);

  const toggleGroup = (groupId) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      next.has(groupId) ? next.delete(groupId) : next.add(groupId);
      return next;
    });
  };

  const handleNav = (tabId) => {
    setActiveTab(tabId);
    if (onClose) onClose();
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed lg:relative z-50 lg:z-auto
          w-60 flex-shrink-0 bg-slate-900 border-r border-slate-800
          flex flex-col h-full lg:h-screen
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* ── Logo ── */}
        <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">F</span>
            </div>
            <div>
              <div className="font-bold text-slate-100 text-base leading-tight">Finovo</div>
              <div className="text-xs text-slate-500">Personal Finance</div>
            </div>
          </div>
          <button
            className="lg:hidden p-1 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Nav groups ── */}
        <nav className="flex-1 py-3 px-3 overflow-y-auto scrollbar-none">
          <div className="space-y-0.5">
            {GROUPS.map(group => {
              // Only render items that actually exist in the tabs prop
              const groupTabs = group.items.map(id => tabById[id]).filter(Boolean);
              if (groupTabs.length === 0) return null;

              const isGroupOpen = openGroups.has(group.id);
              const groupHasActive = groupTabs.some(t => t.id === activeTab);

              return (
                <div key={group.id}>
                  {/* Group header button */}
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-md transition-colors duration-150 group"
                  >
                    <span
                      className={`
                        text-[10.5px] font-bold uppercase tracking-widest select-none
                        transition-colors duration-150
                        ${groupHasActive
                          ? 'text-blue-400'
                          : 'text-slate-600 group-hover:text-slate-500'
                        }
                      `}
                    >
                      {group.label}
                    </span>
                    <ChevronDown
                      size={12}
                      className={`
                        flex-shrink-0 transition-all duration-300 ease-in-out
                        ${isGroupOpen ? 'rotate-0' : '-rotate-90'}
                        ${groupHasActive
                          ? 'text-blue-400'
                          : 'text-slate-600 group-hover:text-slate-500'
                        }
                      `}
                    />
                  </button>

                  {/* Animated body */}
                  <GroupBody open={isGroupOpen}>
                    <div className="pt-0.5 pb-1.5 space-y-0.5">
                      {groupTabs.map(tab => {
                        const Icon = ICONS[tab.id];
                        const active = activeTab === tab.id;
                        return (
                          <button
                            key={tab.id}
                            onClick={() => handleNav(tab.id)}
                            className={`
                              w-full flex items-center gap-2.5 px-3 py-[7px] rounded-lg
                              text-[13px] font-medium transition-all duration-150 group
                              ${active
                                ? 'bg-blue-600 text-white shadow-md shadow-blue-900/40'
                                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/80'
                              }
                            `}
                          >
                            {Icon && (
                              <Icon
                                size={14}
                                className={`
                                  flex-shrink-0 transition-colors duration-150
                                  ${active ? 'text-blue-100' : 'text-slate-500 group-hover:text-slate-400'}
                                `}
                              />
                            )}
                            <span className="flex-1 text-left leading-none">{tab.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </GroupBody>
                </div>
              );
            })}

            {/* ── Pinned bottom items (Settings) ── */}
            <div className="pt-2 mt-1 border-t border-slate-800/60 space-y-0.5">
              {PINNED_BOTTOM.map(id => {
                const tab = tabById[id];
                if (!tab) return null;
                const Icon = ICONS[id];
                const active = activeTab === id;
                return (
                  <button
                    key={id}
                    onClick={() => handleNav(id)}
                    className={`
                      w-full flex items-center gap-2.5 px-3 py-[7px] rounded-lg
                      text-[13px] font-medium transition-all duration-150 group
                      ${active
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-900/40'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/80'
                      }
                    `}
                  >
                    {Icon && (
                      <Icon
                        size={14}
                        className={`
                          flex-shrink-0 transition-colors duration-150
                          ${active ? 'text-blue-100' : 'text-slate-500 group-hover:text-slate-400'}
                        `}
                      />
                    )}
                    <span className="flex-1 text-left leading-none">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </nav>

        {/* ── User footer ── */}
        <div className="px-4 py-4 border-t border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              {displayName[0]?.toUpperCase() ?? 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-200 truncate">{displayName}</div>
              <div className="text-xs text-slate-500 truncate">{displaySub}</div>
            </div>
          </div>
          {onSignOut && (
            <button
              onClick={onSignOut}
              className="mt-2.5 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-600 hover:text-rose-400 hover:bg-slate-800/80 transition-colors duration-150"
            >
              <LogOut size={13} />
              Sign Out
            </button>
          )}
        </div>
      </aside>
    </>
  );
}
